import { useCallback, useState } from "react";
import { Loader2, RefreshCw, Sparkles, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { researchCandidate } from "@/lib/scoutApi";
import { usePollCandidate } from "@/hooks/usePollCandidate";
import { extractApiError } from "@/lib/api";

export const ResearchStatus = ({ c, onChange }) => {
  const building = c.enrich_status === "building";
  const [starting, setStarting] = useState(false);

  const onDone = useCallback(
    (fresh) => {
      onChange(fresh);
      if (fresh.enrich_status === "done") toast.success(`Profile refreshed for ${fresh.name}`);
      else if (fresh.enrich_error) toast.error(fresh.enrich_error);
    },
    [onChange]
  );
  usePollCandidate(c.id, building, "enrich_status", onDone);

  const retry = async () => {
    setStarting(true);
    try {
      await researchCandidate(c.id);
      onChange({ ...c, enrich_status: "building", enrich_error: null });
    } catch (err) {
      toast.error(extractApiError(err, "Could not start research."));
    } finally {
      setStarting(false);
    }
  };

  const fresh = c.researched_at || (c.source !== "manual" ? c.created_at : null);
  const ago = fresh ? Math.floor((Date.now() - new Date(fresh)) / 86400000) : null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="scout-research-status">
      {c.source === "manual" && (
        <span className="inline-flex items-center gap-1 border border-[var(--accent)] text-[var(--accent)] px-2 py-0.5">
          <UserPlus className="w-3 h-3" /> Suggested by Vic
        </span>
      )}
      {building && (
        <span className="inline-flex items-center gap-1 text-slate-500" data-testid="scout-research-building">
          <Loader2 className="w-3 h-3 animate-spin" /> Scout is researching…
        </span>
      )}
      {c.enrich_status === "failed" && (
        <span className="inline-flex items-center gap-2 text-red-600" data-testid="scout-research-failed">
          {c.enrich_error || "Research failed."}
          <button type="button" data-testid="scout-research-retry" onClick={retry} disabled={starting} className="underline hover:text-[#0A192F]">Retry</button>
        </span>
      )}
      {c.name_as_found && <span className="text-slate-500">Found as “{c.name_as_found}”</span>}
      {!building && c.enrich_status !== "failed" && (
        c.source === "manual" && !c.sources?.length ? (
          <button type="button" data-testid="scout-research-start" onClick={retry} disabled={starting}
            className="inline-flex items-center gap-1 text-[var(--accent)] hover:underline">
            <Sparkles className="w-3 h-3" /> Research with the scout
          </button>
        ) : (
          <button type="button" data-testid="scout-research-refresh" onClick={retry} disabled={starting}
            title="Re-run the scout's web research for this person"
            className={`inline-flex items-center gap-1 hover:text-[var(--accent)] ${ago !== null && ago >= 30 ? "text-amber-700" : "text-slate-500"}`}>
            <RefreshCw className="w-3 h-3" /> Refresh profile{ago !== null ? ` · ${ago === 0 ? "researched today" : `${ago}d old`}` : ""}
          </button>
        )
      )}
    </div>
  );
};

export default ResearchStatus;
